import { type CSSProperties } from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, CheckSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DayOutOfRangeSection } from "@/components/calendar-out-of-range-section";
import type { CleaningJob } from "@shared/routes";

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

export interface AdminCleaningDayViewProps {
  dayViewDate: Date;
  today: Date;
  dayViewJobs: CleaningJob[];
  unscheduledDayJobs: CleaningJob[];
  outOfRangeDayJobs: CleaningJob[];
  dayHours: number[];
  getApartmentName: (id: number | null | undefined) => string;
  getApartmentBedrooms: (id: number | null | undefined) => number | null;
  getAssigneeName: (userId: number | null | undefined, orgId?: number | null) => string;
  getCalendarJobColorProps: (job: CleaningJob) => { className: string; style?: CSSProperties };
  getCalendarTaskColorProps: (task: any) => { className: string; style?: CSSProperties };
  getJobsForHour: (hour: number) => CleaningJob[];
  getTasksForDay: (day: Date) => any[];
  formatHour: (hour: number) => string;
  handleCalDragStart: (e: React.DragEvent, jobId: number) => void;
  handleDayViewDrop: (e: React.DragEvent, hour: number, minute?: number) => void;
  getDropMinute: (e: React.DragEvent<HTMLDivElement>) => number;
  dragOverCell: string | null;
  setDragOverCell: (v: string | null) => void;
  navigateDayView: (offset: number) => void;
  setDayViewDate: (d: Date) => void;
  selectedJob: CleaningJob | null;
  setSelectedJob: (j: CleaningJob | null) => void;
  setJobSheetOpen: (v: boolean) => void;
  setEditingTask: (t: any) => void;
  dayOutOfRangeExpanded: boolean;
  setDayOutOfRangeExpanded: (fn: (v: boolean) => boolean) => void;
  users: any[];
  hiddenStatuses: Set<string>;
  hiddenCleaners: Set<number>;
  toggleHiddenStatus: (key: string) => void;
  toggleHiddenCleaner: (id: number) => void;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function cleanerBorderColor(job: CleaningJob, users: any[]): string {
  if (!job.assignedTo) return "transparent";
  return users.find((u: any) => u.id === job.assignedTo)?.color ?? "transparent";
}

/** Compact two-line card for use inside the hour grid */
function JobCard({
  job, aptName, bedrooms, assigneeName, minute, cp, cc, onClick, draggable: drag, onDragStart, testId,
}: {
  job: CleaningJob; aptName: string; bedrooms: number | null; assigneeName: string; minute: number;
  cp: { className: string; style?: CSSProperties }; cc: string;
  onClick: () => void; draggable?: boolean; onDragStart?: (e: React.DragEvent) => void; testId?: string;
}) {
  const brLabel = bedrooms === null ? null : bedrooms === 0 ? "Studio" : `${bedrooms}BR`;
  return (
    <div
      draggable={drag}
      onDragStart={onDragStart}
      onClick={onClick}
      data-testid={testId}
      className={`rounded-md cursor-pointer shadow-sm border-l-4 px-2 py-1.5 min-w-0 ${cp.className}`}
      style={{ ...cp.style, borderLeftColor: cc || undefined }}
      title={`${aptName} — ${assigneeName}\n${STATUS_LABELS[job.status] ?? job.status}${minute > 0 ? ` · :${String(minute).padStart(2, "0")}` : ""}`}
    >
      {/* Line 1: apartment + BR + minute */}
      <div className="flex items-center gap-1 text-xs font-semibold leading-snug min-w-0">
        {job.priority === "urgent" && <AlertTriangle className="w-3 h-3 shrink-0 text-yellow-200" />}
        <span className="truncate">{aptName}</span>
        {brLabel && <span className="opacity-70 shrink-0 font-normal">{brLabel}</span>}
        {minute > 0 && <span className="ml-auto opacity-60 shrink-0 font-normal">:{String(minute).padStart(2, "0")}</span>}
      </div>
      {/* Line 2: cleaner + status */}
      <div className="text-[10px] opacity-80 leading-snug mt-0.5 truncate">
        {assigneeName.split(" ")[0]} · {STATUS_LABELS[job.status] ?? job.status}
      </div>
    </div>
  );
}

/**
 * Renders a grid of job cards: 2 columns on mobile, 3 on desktop, wraps to new rows.
 * All jobs visible — no truncation, no popovers.
 */
function JobGrid({ jobs, tasks, getApartmentName, getApartmentBedrooms, getAssigneeName,
  getCalendarJobColorProps, getCalendarTaskColorProps, users, onJobClick, onTaskClick, draggable }: {
  jobs: CleaningJob[]; tasks: any[];
  getApartmentName: (id: number | null | undefined) => string;
  getApartmentBedrooms: (id: number | null | undefined) => number | null;
  getAssigneeName: (id: number | null | undefined, orgId?: number | null) => string;
  getCalendarJobColorProps: (job: CleaningJob) => { className: string; style?: CSSProperties };
  getCalendarTaskColorProps: (task: any) => { className: string; style?: CSSProperties };
  users: any[]; onJobClick: (job: CleaningJob) => void;
  onTaskClick: (task: any) => () => void; draggable?: boolean;
}) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-2">
      {jobs.map(job => {
        const jm = job.scheduledDate ? new Date(job.scheduledDate).getMinutes() : 0;
        return (
          <JobCard
            key={job.id} job={job}
            aptName={getApartmentName(job.apartmentId)}
            bedrooms={getApartmentBedrooms(job.apartmentId)}
            assigneeName={getAssigneeName(job.assignedTo, (job as any).assignedOrgId)}
            minute={jm}
            cp={getCalendarJobColorProps(job)}
            cc={cleanerBorderColor(job, users)}
            onClick={() => onJobClick(job)}
            draggable={draggable}
            onDragStart={draggable ? e => { e.dataTransfer.setData("text/plain", String(job.id)); e.dataTransfer.effectAllowed = "move"; } : undefined}
            testId={`day-job-${job.id}`}
          />
        );
      })}
      {tasks.map((task: any) => {
        const cp = getCalendarTaskColorProps(task);
        return (
          <div key={`gt-${task.id}`}
            className={`rounded-md px-2 py-1.5 text-xs font-medium cursor-pointer border border-dashed border-white/40 min-w-0 ${cp.className}`}
            style={cp.style} onClick={onTaskClick(task)} data-testid={`day-hour-task-${task.id}`}>
            <div className="flex items-center gap-1 leading-snug">
              <CheckSquare className="w-3 h-3 shrink-0" />
              <span className="truncate font-semibold">{task.title}</span>
            </div>
            {task.assignedTo && (
              <div className="text-[10px] opacity-80 leading-snug mt-0.5 truncate">
                {getAssigneeName(task.assignedTo).split(" ")[0]}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function AdminCleaningDayView(props: AdminCleaningDayViewProps) {
  const {
    dayViewDate, today, dayViewJobs, unscheduledDayJobs, outOfRangeDayJobs, dayHours,
    getApartmentName, getApartmentBedrooms, getAssigneeName,
    getCalendarJobColorProps, getCalendarTaskColorProps,
    getJobsForHour, getTasksForDay, formatHour,
    handleCalDragStart, handleDayViewDrop, getDropMinute, dragOverCell, setDragOverCell,
    navigateDayView, setDayViewDate,
    setSelectedJob, setJobSheetOpen, setEditingTask,
    dayOutOfRangeExpanded, setDayOutOfRangeExpanded,
    users, hiddenStatuses, hiddenCleaners, toggleHiddenStatus, toggleHiddenCleaner,
  } = props;

  const dayTasks = getTasksForDay(dayViewDate);
  const hasContent = dayViewJobs.length > 0 || dayTasks.length > 0;
  const openJob = (job: CleaningJob) => { setSelectedJob(job); setJobSheetOpen(true); };
  const makeTaskClick = (task: any) => () => setEditingTask({
    ...task,
    scheduledAt: task.scheduledAt ? new Date(task.scheduledAt).toISOString().split("T")[0] : "",
    scheduledTime: task.scheduledAt ? new Date(task.scheduledAt).toTimeString().slice(0, 5) : "",
  });

  const allDayTasks = dayTasks.filter((t: any) => {
    if (!t.scheduledAt) return true;
    const d = new Date(t.scheduledAt);
    return d.getHours() === 0 && d.getMinutes() === 0;
  });

  return (
    <>
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div>
          <h2 className="text-base font-semibold">
            {dayViewDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </h2>
          <p className="text-xs text-muted-foreground">{dayViewJobs.length} job{dayViewJobs.length !== 1 ? "s" : ""} scheduled</p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" onClick={() => navigateDayView(-1)} data-testid="button-day-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); setDayViewDate(d); }} data-testid="button-day-today">
            Today
          </Button>
          <Button variant="outline" size="icon" onClick={() => navigateDayView(1)} data-testid="button-day-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {!hasContent && (
        <div className="p-8 text-center text-muted-foreground text-sm">No cleaning jobs or tasks scheduled for this day</div>
      )}

      {hasContent && (
        <div className="overflow-y-auto max-h-[70vh]">

          {/* Unscheduled / All Day */}
          {(unscheduledDayJobs.length > 0 || allDayTasks.length > 0) && (
            <div className="border-b bg-muted/30">
              <div className="flex items-center gap-2 px-3 pt-2">
                <span className="text-xs font-semibold text-muted-foreground">Unscheduled / All Day</span>
                <span className="text-[10px] text-muted-foreground opacity-60">({unscheduledDayJobs.length + allDayTasks.length})</span>
              </div>
              <JobGrid
                jobs={unscheduledDayJobs} tasks={allDayTasks}
                getApartmentName={getApartmentName} getApartmentBedrooms={getApartmentBedrooms}
                getAssigneeName={getAssigneeName} getCalendarJobColorProps={getCalendarJobColorProps}
                getCalendarTaskColorProps={getCalendarTaskColorProps} users={users}
                onJobClick={openJob} onTaskClick={makeTaskClick} draggable
              />
            </div>
          )}

          {/* Out-of-range */}
          <DayOutOfRangeSection
            jobs={outOfRangeDayJobs} expanded={dayOutOfRangeExpanded}
            onToggle={() => setDayOutOfRangeExpanded(v => !v)}
            onDragStart={(e, id) => handleCalDragStart(e, id)}
            getJobColorProps={getCalendarJobColorProps}
            getAptName={getApartmentName} getAptBedrooms={getApartmentBedrooms}
            getAssigneeName={getAssigneeName} onJobClick={openJob}
          />

          {/* Hour rows — all hours from org settings shown so drag-drop targets are always available */}
          {dayHours.map(hour => {
            const hourJobs = getJobsForHour(hour).slice().sort((a, b) => {
              const ma = a.scheduledDate ? new Date(a.scheduledDate).getMinutes() : 0;
              const mb = b.scheduledDate ? new Date(b.scheduledDate).getMinutes() : 0;
              return ma - mb;
            });
            const hourTasks = dayTasks.filter((t: any) => {
              if (!t.scheduledAt) return false;
              const d = new Date(t.scheduledAt);
              return d.getHours() === hour && !(d.getHours() === 0 && d.getMinutes() === 0);
            });
            const isEmpty = !hourJobs.length && !hourTasks.length;
            const cellKey = `day-${hour}`;
            const isCurrent = isSameDay(dayViewDate, today) && new Date().getHours() === hour;

            return (
              <div
                key={hour}
                className={`border-b last:border-b-0 transition-colors ${isCurrent ? "bg-primary/5" : ""} ${dragOverCell === cellKey ? "bg-primary/15 ring-1 ring-primary/40 ring-inset" : ""}`}
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverCell(cellKey); }}
                onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverCell(null); }}
                onDrop={e => handleDayViewDrop(e, hour, getDropMinute(e))}
                data-testid={`day-hour-${hour}`}
              >
                {isEmpty ? (
                  /* Empty hour — compact drop target, no clutter */
                  <div className={`flex items-center px-3 h-9 ${isCurrent ? "text-primary" : "text-muted-foreground"}`}>
                    <span className={`text-xs ${isCurrent ? "font-bold" : "opacity-50"}`}>{formatHour(hour)}</span>
                  </div>
                ) : (
                  <>
                    {/* Hour label + count */}
                    <div className={`flex items-center gap-2 px-3 pt-2 ${isCurrent ? "text-primary" : "text-muted-foreground"}`}>
                      <span className={`text-xs font-semibold ${isCurrent ? "font-bold" : ""}`}>{formatHour(hour)}</span>
                      <span className="text-[10px] opacity-60">({hourJobs.length + hourTasks.length})</span>
                    </div>
                    {/* Grid of cards — 2 cols mobile, 3 cols desktop */}
                    <JobGrid
                      jobs={hourJobs} tasks={hourTasks}
                      getApartmentName={getApartmentName} getApartmentBedrooms={getApartmentBedrooms}
                      getAssigneeName={getAssigneeName} getCalendarJobColorProps={getCalendarJobColorProps}
                      getCalendarTaskColorProps={getCalendarTaskColorProps} users={users}
                      onJobClick={openJob} onTaskClick={makeTaskClick} draggable
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Legend ── */}
      <div className="flex items-center gap-4 px-3 py-2 text-xs text-muted-foreground border-t flex-wrap">
        {([
          { key: "pending_no_cleaner", label: "Pending (no cleaner)", bg: "bg-red-500/80" },
          { key: "in_progress", label: "In Progress", bg: "bg-blue-500/80" },
          { key: "completed", label: "Completed", bg: "bg-green-500/80" },
          { key: "cancelled", label: "Cancelled", bg: "bg-gray-400/80" },
        ] as const).map(({ key, label, bg }) => {
          const hidden = hiddenStatuses.has(key);
          return (
            <button key={key} onClick={() => toggleHiddenStatus(key)} className={`flex items-center gap-1.5 transition-opacity ${hidden ? "opacity-40" : "opacity-100"}`}>
              <div className={`w-3 h-3 rounded relative ${bg}`}>
                {hidden && <div className="absolute inset-0 flex items-center justify-center"><div className="w-[11px] h-[1.5px] bg-white rotate-45" /></div>}
              </div>
              <span className={hidden ? "line-through" : ""}>{label}</span>
            </button>
          );
        })}
        {users.filter((u: any) => (u.isActive !== false) && u.color && (u.role === "cleaner" || u.role === "maintenance")).map((u: any) => {
          const hidden = hiddenCleaners.has(u.id);
          return (
            <button key={u.id} onClick={() => toggleHiddenCleaner(u.id)} className={`flex items-center gap-1.5 transition-opacity ${hidden ? "opacity-40" : "opacity-100"}`}>
              <div className="w-3 h-3 rounded relative" style={{ backgroundColor: u.color }}>
                {hidden && <div className="absolute inset-0 flex items-center justify-center"><div className="w-[11px] h-[1.5px] bg-white rotate-45" /></div>}
              </div>
              <span className={hidden ? "line-through" : ""}>{u.fullName || u.username}</span>
            </button>
          );
        })}
        <span className="ml-auto hidden md:inline">Drag jobs to reschedule</span>
      </div>
    </>
  );
}
